/**
 * A SetMap extends the standard Map class with an add method so that its values
 * are always a Set. This is useful for grouping objects under a common key.
 */
export class SetMap<K, V> extends Map<K, Set<V>> {

    /**
     * Adds a value into the Set instance that is stored under the given key. If
     * this is the first value associated with this key, then the underlying Set
     * is created.
     * @param key - the key object; since this is a Map it can be any type
     * @param value - the stored item
     * @returns - returns the map object
     */
    add(key: K, value: V) {
        if(this.has(key)) {
            let s = this.get(key) as Set<V>;
            s.add(value);
        } else {
            let s: Set<V> = new Set();
            s.add(value);
            this.set(key, s);
        }
        return this;
    }

    /**
     * Deletes the given value from the underlying set. Note that this will not
     * leave an empty set under the given key, so the `has` method will
     * correctly tell you whether all the entries under the key have been
     * removed.
     * @param key - the key object; since this is a Map it can be any type
     * @param value - the stored item
     * @returns - true if the item was an element of the set under the
     * given key, false otherwise.
     */
    deleteValue(key: K, value: V) {
        if(this.has(key)) {
            let s = this.get(key) as Set<V>;
            var result = s.delete(value);
            if(s.size == 0)
                super.delete(key);
            return result;
        } else {
            return false;
        }
    }

    /**
     * Create copies of the Map and all of its contained Sets. This does not
     * copy any of the keys or values contained therein.
     * @returns A new SetMap containing all of the same key and value
     * references.
     */
    clone() {
        const result = new SetMap();
        for(let key of this.keys()) {
            let value = new Set(this.get(key));
            result.set(key, value);
        }
        return result;
    }

    /**
     * Generates the revesre mapping such that each value becomes a key and
     * each key becomes a value.
     */
    reverse(): SetMap<V, K> {
        const result: SetMap<V, K> = new SetMap();
        for(let [m, ns] of this.entries()){
            for(let n of ns) {
                result.add(n, m);
            }
        }
        return result;
    }

    /**
     * Visits every value in a single iteration. If the same value is listed
     * under multiple keys, then that value will be visited multiple times.
     */
    *valuesFlat(): Generator<V, void, unknown> {
        for(const s of this.values()) {
            for(const item of s) {
                yield item;
            }
        }
    }

}
export default SetMap;
